import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AlertTriangle, CalendarClock, CheckCircle2, CircleDollarSign, Clock3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import {
  approveCreatorAllocation,
  createCollabPaymentCheckout,
  createCollabPaymentIntent,
  getStripeConfig,
  getCollabPaymentStatus,
  getCreatorWallet,
  getPaidCollabFlowConfig,
  reportCreatorAllocationIssue,
  requestCollabPositionReduction,
  requestCreatorAllocationRefund,
  quoteCollabPaymentMethod,
} from '../../services/api';
import styles from './PaidCollabPaymentStatus.module.scss';

const approvedStates = new Set(['manually_approved', 'automatically_approved']);
const terminalPaymentStates = new Set(['paid', 'partially_refunded', 'fully_refunded', 'disputed']);
const failedPaymentStates = new Set(['payment_failed']);
const awaitingStripeStates = new Set(['processing', 'requires_action']);
const openRefundStates = new Set(['requested', 'under_review', 'approved_full', 'approved_partial', 'processing']);

const money = (amount, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(amount || 0) / 100);

const majorMoney = (amount, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(amount || 0));

export const PaidCollabCardPayment = ({ collabId, setup, onComplete, onCancel }) => {
  const { t } = useTranslation('collab');
  const stripe = useStripe();
  const elements = useElements();
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const collectPaymentMethod = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError('');
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({ elements });
      if (methodError) throw methodError;
      const response = await quoteCollabPaymentMethod(collabId, {
        payment_method_id: paymentMethod.id,
        attempt_token: setup.attempt_token,
      });
      // Keep the selected method locally so a reused PaymentIntent is
      // confirmed with the same method that was just quoted.
      setQuote({ ...response.data, payment_method_id: paymentMethod.id });
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'We could not prepare this payment.');
    } finally {
      setBusy(false);
    }
  };

  const confirmPayment = async () => {
    if (!stripe || !elements || !quote || busy) return;
    setBusy(true);
    setError('');
    try {
      await elements.update({ amount: quote.final_amount });
      const response = await createCollabPaymentIntent(collabId, { quote_token: quote.quote_token });
      const intentStatus = response.data.status;
      if (intentStatus === 'succeeded' || intentStatus === 'processing') {
        // The backend may return an existing PaymentIntent after a refresh or
        // a retry. Confirming an already-processing/succeeded intent causes a
        // Stripe 400 and must never be attempted again.
        onComplete(intentStatus);
        return;
      }
      if (!['requires_confirmation', 'requires_action', 'requires_payment_method'].includes(intentStatus)) {
        throw new Error('Stripe returned an unexpected payment status.');
      }
      const result = await stripe.confirmCardPayment(
        response.data.client_secret,
        quote.payment_method_id ? { payment_method: quote.payment_method_id } : undefined,
      );
      if (result.error) throw result.error;
      const paymentStatus = result.paymentIntent?.status;
      if (!['succeeded', 'processing', 'requires_action'].includes(paymentStatus)) {
        throw new Error('Stripe has not confirmed this payment yet.');
      }
      onComplete(paymentStatus);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'We could not confirm this payment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.paymentForm}>
      {!quote ? (
        <form onSubmit={collectPaymentMethod}>
          <PaymentElement options={{ layout: 'accordion' }} />
          <button className={styles.refund} type="submit" disabled={!stripe || busy}>
            {busy ? t('payment_release.payment_preparing') : t('payment_release.payment_review_amount')}
          </button>
        </form>
      ) : (
        <div className={styles.quote}>
          <div><span>{t('payment_release.payment_creator_compensation')}</span><strong>{money(quote.creator_compensation, quote.currency)}</strong></div>
          <div><span>{t('payment_release.payment_foth_fee')}</span><strong>{money(quote.foth_service_fee, quote.currency)}</strong></div>
          <div><span>{t('payment_release.payment_payout_fee')}</span><strong>{money(quote.payout_fee, quote.currency)}</strong></div>
          <div><span>{t('payment_release.payment_processing')}</span><strong>{money(quote.payment_processing, quote.currency)}</strong></div>
          <div className={styles.quoteTotal}><span>{t('payment_release.payment_total')}</span><strong>{money(quote.final_amount, quote.currency)}</strong></div>
          <p>{t('payment_release.payment_creator_net_note')}</p>
          <div className={styles.actions}>
            <button className={styles.refund} type="button" disabled={busy} onClick={confirmPayment}>
              {busy ? t('payment_release.payment_confirming') : t('payment_release.payment_confirm', { amount: money(quote.final_amount, quote.currency) })}
            </button>
            <button className={styles.secondary} type="button" disabled={busy} onClick={() => setQuote(null)}>
              {t('payment_release.payment_change_method')}
            </button>
          </div>
        </div>
      )}
      {error && <p className={styles.paymentError} role="alert">{error}</p>}
      <button className={styles.cancelPayment} type="button" disabled={busy} onClick={onCancel}>{t('payment_release.payment_cancel')}</button>
    </div>
  );
};

const PaidCollabPaymentStatus = ({ collabId, role, collab = null, onPaymentChange }) => {
  const { t, i18n } = useTranslation('collab');
  const [payment, setPayment] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [flowConfig, setFlowConfig] = useState(null);
  const [creatorWallet, setCreatorWallet] = useState(null);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [paymentSetup, setPaymentSetup] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [paymentStatusTimedOut, setPaymentStatusTimedOut] = useState(false);

  const syncPayment = useCallback((paymentData) => {
    setPayment(paymentData);
    onPaymentChange?.(paymentData);
    return paymentData;
  }, [onPaymentChange]);

  const load = useCallback(async () => {
    if (!collabId) return null;

    if (role === 'creator') {
      let configResponse;
      try {
        configResponse = await getPaidCollabFlowConfig();
        setFlowConfig(configResponse.data);
      } catch (configError) {
        setFlowConfig(null);
        setCreatorWallet(null);
        setPayment(null);
        return null;
      }

      if (!configResponse.data?.enabled) {
        setPayment(null);
        setCreatorWallet(null);
        return null;
      }

      let paymentData = null;
      try {
        const response = await getCollabPaymentStatus(collabId);
        paymentData = response.data;
        syncPayment(paymentData);
      } catch (error) {
        if (error.response?.status !== 404) console.error('Could not load paid collab status', error);
        setPayment(null);
      }

      try {
        const walletResponse = await getCreatorWallet();
        setCreatorWallet(walletResponse.data);
      } catch (walletError) {
        console.error('Could not load creator payout wallet', walletError);
        setCreatorWallet(null);
      }
      return paymentData;
    }

    try {
      const configResponse = await getPaidCollabFlowConfig();
      setFlowConfig(configResponse.data);
    } catch (configError) {
      setFlowConfig(null);
    }

    let paymentData = null;
    try {
      const response = await getCollabPaymentStatus(collabId);
      paymentData = response.data;
      syncPayment(paymentData);
    } catch (error) {
      if (error.response?.status !== 404) console.error('Could not load paid collab status', error);
      setPayment(null);
    }
    return paymentData;
  }, [collabId, role, syncPayment]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (role !== 'business' || !paymentSubmitted || paymentStatusTimedOut) return undefined;
    let cancelled = false;
    let timeoutId;
    let attempts = 0;

    const poll = async () => {
      const latestPayment = await load();
      if (cancelled) return;
      const latestStatus = latestPayment?.payment_status;
      if (terminalPaymentStates.has(latestStatus)) {
        setPaymentSubmitted(false);
        setPaymentStatusTimedOut(false);
        if (latestStatus === 'paid') toast.success(t('payment_release.payment_confirmed'));
        return;
      }
      if (failedPaymentStates.has(latestStatus)) {
        setPaymentSubmitted(false);
        setPaymentStatusTimedOut(false);
        return;
      }
      if (attempts >= 20) {
        setPaymentStatusTimedOut(true);
        return;
      }
      attempts += 1;
      timeoutId = window.setTimeout(poll, 3000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [load, paymentStatusTimedOut, paymentSubmitted, role, t]);

  const allocations = useMemo(() => {
    if (!payment) return [];
    return Array.isArray(payment.allocations) ? payment.allocations : [payment];
  }, [payment]);
  const paymentFailed = Boolean(payment && failedPaymentStates.has(payment.payment_status));
  const paymentPending = Boolean(
    payment && !terminalPaymentStates.has(payment.payment_status) && !paymentFailed,
  );
  const creatorTotal = Number(payment?.creator_total || 0);
  const requiredCreatorCount = Number(collab?.quantity || 0);
  const plannedCreatorTotal = Number(collab?.cash_compensation ?? collab?.sponsor_amount ?? 0) * requiredCreatorCount;
  const releasedAmount = allocations.reduce(
    (total, allocation) => total + (allocation.transfer_status === 'transferred'
      ? Number(allocation.creator_amount || 0)
      : 0),
    0,
  );
  const pendingReleaseAmount = Math.max(creatorTotal - releasedAmount, 0);
  const allocationsUnderReview = allocations.filter(
    (allocation) => allocation.approval_status === 'under_business_review',
  ).length;
  const positionReduction = payment?.refund_requests?.find(
    (refund) => refund.refund_scope === 'unused_position' && openRefundStates.has(refund.status),
  );
  const canReducePositions = requiredCreatorCount > allocations.length;

  const exactDate = (value) => {
    if (!value) return t('payment_release.date_not_available');
    return new Intl.DateTimeFormat(i18n.language || 'en', {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(value));
  };

  const payoutAvailableAt = (allocation) => {
    if (allocation?.payout_available_at) return allocation.payout_available_at;
    const dates = [];
    if (!allocation?.refund_window_closed_at && allocation?.refund_request_deadline) dates.push(allocation.refund_request_deadline);
    if (allocation?.transfer_release_at) dates.push(allocation.transfer_release_at);
    return dates.length ? dates.sort((first, second) => new Date(second) - new Date(first))[0] : null;
  };

  const nextPayoutAvailableAt = allocations
    .map(payoutAvailableAt)
    .filter(Boolean)
    .sort((first, second) => new Date(first) - new Date(second))[0];

  const run = async (allocationId, action) => {
    try {
      setBusyId(allocationId);
      await action();
      await load();
      toast.success(t('payment_release.action_saved'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('payment_release.action_error'));
    } finally {
      setBusyId(null);
    }
  };

  const reportIssue = (allocation) => {
    const reason = window.prompt(t('payment_release.issue_reason_prompt'));
    if (!reason) return;
    const explanation = window.prompt(t('payment_release.issue_explanation_prompt'));
    if (!explanation) return;
    run(allocation.id, () => reportCreatorAllocationIssue(allocation.id, { reason, explanation, evidence: [] }));
  };

  const requestRefund = (allocation) => {
    const explanation = window.prompt(t('payment_release.refund_explanation_prompt'));
    if (!explanation) return;
    run(allocation.id, () => requestCreatorAllocationRefund(allocation.id, {
      reason: 'creator_material_non_compliance', explanation, evidence: [],
    }));
  };

  const requestPositionReduction = () => {
    const requested = window.prompt(
      t('payment_release.position_reduction_prompt', { current: requiredCreatorCount }),
      String(Math.max(requiredCreatorCount - 1, 1)),
    );
    if (requested === null) return;
    const quantity = Number.parseInt(requested, 10);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity >= requiredCreatorCount || quantity < allocations.length) {
      toast.error(t('payment_release.position_reduction_invalid', { accepted: allocations.length }));
      return;
    }
    run('position-reduction', () => requestCollabPositionReduction(collabId, { quantity }));
  };

  const startCheckout = async () => {
    if (!acceptedPolicies || !flowConfig?.enabled) return;
    try {
      setBusyId('checkout');
      const stripeConfig = await getStripeConfig();
      if (!stripeConfig.data?.publishable_key) throw new Error('Stripe payment configuration is unavailable.');
      const response = await createCollabPaymentCheckout(collabId, {
        accepted: true,
        terms_version: flowConfig.terms_version,
        refund_policy_version: flowConfig.refund_policy_version,
      });
      setPaymentSetup(response.data);
      setStripePromise(loadStripe(stripeConfig.data.publishable_key));
      setBusyId(null);
    } catch (error) {
      toast.error(error.response?.data?.error || t('payment_release.checkout_error'));
      setBusyId(null);
    }
  };

  const cancelPayment = () => {
    setPaymentSetup(null);
    setStripePromise(null);
    setBusyId(null);
  };

  const completePayment = (stripeStatus) => {
    setPaymentSetup(null);
    setStripePromise(null);
    setPaymentSubmitted(true);
    setPaymentStatusTimedOut(false);
    // The client-side status is only an initial signal. The polling below
    // waits for the backend/webhook-confirmed status before showing success.
    if (stripeStatus === 'processing' || stripeStatus === 'requires_action') {
      toast.info(t('payment_release.payment_pending'));
    }
    load();
  };

  const retryPayment = () => {
    setPaymentSubmitted(false);
    setPaymentStatusTimedOut(false);
    setPaymentSetup(null);
    setStripePromise(null);
  };

  const paymentPanel = role === 'business' && flowConfig?.enabled && (
    <section className={styles.panel} aria-label={t('payment_release.checkout_title')}>
      <header><div className={styles.icon}><CircleDollarSign size={20} /></div><div><span>{t('payment_release.eyebrow')}</span><h3>{t('payment_release.checkout_title')}</h3></div></header>
      <p>{paymentPending ? t('payment_release.payment_pending_review') : t('payment_release.checkout_help')}</p>
      {requiredCreatorCount > 0 && (!payment || !terminalPaymentStates.has(payment?.payment_status)) && (
        <div className={styles.checkoutSummary}>
          <div><span>{t('payment_release.creator_funding_total')}</span><strong>{majorMoney(plannedCreatorTotal, collab?.cash_currency || 'USD')}</strong></div>
          {paymentSetup && <>
            <div><span>{t('payment_release.platform_and_payout_fees')}</span><strong>{money(paymentSetup.business_subtotal - paymentSetup.creator_compensation, paymentSetup.currency)}</strong></div>
            <small>{t('payment_release.payment_fee_detail')}</small>
          </>}
          {!paymentSetup && <small>{t('payment_release.creator_funding_detail', { count: requiredCreatorCount })}</small>}
        </div>
      )}
      {!paymentSetup && (
        <>
          <label className={styles.acceptance}>
            <input type="checkbox" checked={acceptedPolicies} onChange={(event) => setAcceptedPolicies(event.target.checked)} />
            <span>{t('payment_release.checkout_accept')}</span>
          </label>
          <button className={styles.refund} disabled={!acceptedPolicies || busyId === 'checkout'} onClick={startCheckout}>
            {busyId === 'checkout' ? t('payment_release.checkout_loading') : t('payment_release.checkout_action')}
          </button>
        </>
      )}
      {paymentSetup && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{
            mode: 'payment',
            amount: paymentSetup.business_subtotal,
            currency: 'usd',
            paymentMethodCreation: 'manual',
            paymentMethodTypes: ['card'],
          }}
        >
          <PaidCollabCardPayment
            collabId={collabId}
            setup={paymentSetup}
            onComplete={completePayment}
            onCancel={cancelPayment}
          />
        </Elements>
      )}
    </section>
  );

  const startCreatorOnboarding = async () => {
    window.location.assign('/creator/wallet');
  };

  const globalPayoutStatus = creatorWallet?.global_payouts_status || 'not_started';
  const creatorCanStartOnboarding = creatorWallet
    && globalPayoutStatus !== 'ready'
    && ['not_started', 'onboarding', 'action_required', 'restricted'].includes(globalPayoutStatus);

  const creatorWalletNotice = globalPayoutStatus === 'restricted'
    ? t('payment_release.wallet_restricted')
    : globalPayoutStatus === 'action_required'
      ? t('payment_release.wallet_information_required')
      : globalPayoutStatus === 'under_review'
        ? t('payment_release.wallet_in_review')
        : t('payment_release.wallet_setup_help');

  const creatorWalletPanel = role === 'creator' && flowConfig?.enabled && creatorWallet && globalPayoutStatus !== 'ready' && (
    <div className={styles.walletSetup} role="status">
      <div className={styles.walletSetupCopy}>
        <strong>{t('payment_release.wallet_setup_title')}</strong>
        <p>{creatorWalletNotice}</p>
      </div>
      {creatorCanStartOnboarding && (
        <button
          className={styles.refund}
          disabled={busyId === 'creator-wallet'}
          onClick={startCreatorOnboarding}
        >
          {busyId === 'creator-wallet' ? t('payment_release.wallet_opening') : t('payment_release.wallet_setup_action')}
        </button>
      )}
    </div>
  );

  if (role === 'business' && flowConfig?.enabled && paymentFailed) {
    return (
      <section className={styles.panel} aria-label={t('payment_release.checkout_title')}>
        <header><div className={styles.icon}><AlertTriangle size={20} /></div><div><span>{t('payment_release.eyebrow')}</span><h3>{t('payment_release.payment_failed_title')}</h3></div></header>
        <p role="alert">{t('payment_release.payment_failed_help')}</p>
        <button className={styles.refund} type="button" onClick={retryPayment}>
          {t('payment_release.payment_retry')}
        </button>
      </section>
    );
  }

  if (role === 'business' && flowConfig?.enabled && (!payment || !terminalPaymentStates.has(payment.payment_status))) {
    if (paymentSubmitted || awaitingStripeStates.has(payment?.payment_status)) {
      return (
        <section className={styles.panel} aria-label={t('payment_release.checkout_title')}>
          <header><div className={styles.icon}><CircleDollarSign size={20} /></div><div><span>{t('payment_release.eyebrow')}</span><h3>{t('payment_release.checkout_title')}</h3></div></header>
          <p>{paymentStatusTimedOut ? t('payment_release.payment_status_timeout') : t('payment_release.payment_submitted')}</p>
          {paymentStatusTimedOut && (
            <button className={styles.secondary} type="button" onClick={() => setPaymentStatusTimedOut(false)}>
              {t('payment_release.payment_refresh_status')}
            </button>
          )}
        </section>
      );
    }
    return paymentPanel;
  }

  if (!payment || !allocations.length) {
    if (role === 'creator') return creatorWalletPanel;
    if (role !== 'business' || !flowConfig?.enabled) return null;
    return (
      <section className={styles.panel} aria-label={t('payment_release.checkout_title')}>
        <header><div className={styles.icon}><CircleDollarSign size={20} /></div><div><span>{t('payment_release.eyebrow')}</span><h3>{t('payment_release.checkout_title')}</h3></div></header>
        <p>{t('payment_release.checkout_help')}</p>
        <label className={styles.acceptance}>
          <input type="checkbox" checked={acceptedPolicies} onChange={(event) => setAcceptedPolicies(event.target.checked)} />
          <span>{t('payment_release.checkout_accept')}</span>
        </label>
        <button className={styles.refund} disabled={!acceptedPolicies || busyId === 'checkout'} onClick={startCheckout}>
          {t('payment_release.checkout_action')}
        </button>
      </section>
    );
  }

  return (
    <section className={styles.panel} aria-label={t('payment_release.title')}>
      <header>
        <div className={styles.icon}><CircleDollarSign size={20} /></div>
        <div>
          <span>{t('payment_release.eyebrow')}</span>
          <h3>{role === 'business' ? t('payment_release.business_title') : t('payment_release.title')}</h3>
        </div>
      </header>
      {role === 'business' && payment?.payment_status === 'paid' && (
        <>
          <div className={styles.businessOverview} role="status">
            <div className={styles.businessOverviewCopy}>
              <span className={styles.statusBadge}><CheckCircle2 size={16} /> {t('payment_release.business_funded')}</span>
              <strong>{t('payment_release.payment_confirmed')}</strong>
              <p>{t('payment_release.business_funded_help')}</p>
            </div>
            <div className={styles.businessOverviewAmount}>
              <span>{t('payment_release.business_creator_budget')}</span>
              <strong>{majorMoney(creatorTotal, payment.currency)}</strong>
            </div>
          </div>
          <div className={styles.businessMetrics}>
            <div><span>{t('payment_release.business_creators')}</span><strong>{allocations.length}</strong></div>
            <div><span>{t('payment_release.business_pending_release')}</span><strong>{majorMoney(pendingReleaseAmount, payment.currency)}</strong></div>
            <div><span>{t('payment_release.business_released')}</span><strong>{majorMoney(releasedAmount, payment.currency)}</strong></div>
          </div>
          <div className={styles.nextStep}>
            <span>{t('payment_release.business_next_step')}</span>
            <strong>{allocationsUnderReview
              ? t('payment_release.business_review_action', { count: allocationsUnderReview })
              : nextPayoutAvailableAt
                ? t('payment_release.business_protection_action_with_date', { date: exactDate(nextPayoutAvailableAt) })
                : t('payment_release.business_protection_action')}</strong>
          </div>
          <div className={styles.actions}>
            {positionReduction ? (
              <p>{t('payment_release.position_reduction_pending', { quantity: positionReduction.adjustment_data?.requested_quantity })}</p>
            ) : (
              <button
                className={styles.secondary}
                disabled={!canReducePositions || busyId === 'position-reduction'}
                onClick={requestPositionReduction}
              >
                {t('payment_release.position_reduction_action')}
              </button>
            )}
            {!positionReduction && !canReducePositions && <p>{t('payment_release.position_reduction_unavailable')}</p>}
          </div>
        </>
      )}
      {role === 'creator' && paymentPending && <p>{t('payment_release.payment_pending_review')}</p>}
      {creatorWalletPanel}
      {role === 'business' && (
        <div className={styles.allocationsHeading}>
          <div><span>{t('payment_release.business_breakdown_eyebrow')}</span><h4>{t('payment_release.business_breakdown_title')}</h4></div>
          <span>{t('payment_release.business_creator_count', { count: allocations.length })}</span>
        </div>
      )}
      <div className={styles.allocations}>
        {allocations.map((allocation) => {
          const isApproved = approvedStates.has(allocation.approval_status);
          const refundOpen = allocation.refund_request_open;
          const isDeliverablesComplete = allocation.deliverables_summary
            ? (allocation.deliverables_summary.mode === 'defined'
                ? allocation.deliverables_summary.is_complete
                : allocation.deliverables_summary.has_submissions)
            : true;
          const isUnderReview = allocation.approval_status === 'under_business_review';
          const isAwaiting = allocation.approval_status === 'awaiting_delivery';

          return (
            <article key={allocation.id} className={styles.allocation}>
              <div className={styles.allocationTop}>
                <div>
                  {role === 'business' && <strong>@{allocation.creator_username}</strong>}
                  <span className={`${styles.status} ${styles[`status_${allocation.transfer_status}`] || ''}`}>
                    {t(`payment_release.status.${allocation.transfer_status}`)}
                  </span>
                </div>
                <strong>{majorMoney(allocation.creator_amount, allocation.currency)}</strong>
              </div>

              {isAwaiting && (
                <p className={styles.awaitingNotice}>
                  <Clock3 size={17} /> {role === 'business'
                    ? (allocation.deliverables_summary && allocation.deliverables_summary.mode === 'defined'
                        ? t('payment_release.business_awaiting_progress', {
                            creator: allocation.creator_username,
                            submitted: allocation.deliverables_summary.submitted_total,
                            required: allocation.deliverables_summary.required_total,
                          })
                        : t('payment_release.business_awaiting_delivery', { creator: allocation.creator_username }))
                    : t('payment_release.creator_awaiting_delivery')}
                </p>
              )}

              {isUnderReview && (
                <>
                  {role === 'business' ? (
                    <div className={styles.reviewDeadlineWarning} role="alert">
                      <AlertTriangle size={18} />
                      <div>
                        <strong>{t('payment_release.business_review_warning_title', 'Tiempo límite de revisión: 3 días hábiles')}</strong>
                        <p>{t('payment_release.business_review_warning_body', {
                          deadline: exactDate(allocation.business_review_deadline),
                          creator: allocation.creator_username,
                        })}</p>
                      </div>
                    </div>
                  ) : (
                    <p><CalendarClock size={17} /> {t('payment_release.creator_review', { deadline: exactDate(allocation.business_review_deadline) })}</p>
                  )}
                </>
              )}

              {isApproved && !refundOpen && (
                <p><CheckCircle2 size={17} /> {role === 'business'
                  ? t('payment_release.business_approved', { approved: exactDate(allocation.approved_at), deadline: exactDate(allocation.refund_request_deadline), available: exactDate(payoutAvailableAt(allocation)) })
                  : t('payment_release.creator_approved', { release: exactDate(payoutAvailableAt(allocation)), deadline: exactDate(allocation.refund_request_deadline) })}</p>
              )}
              {refundOpen && (
                <p><AlertTriangle size={17} /> {role === 'business'
                  ? t('payment_release.refund_under_review_business')
                  : t('payment_release.refund_under_review_creator')}</p>
              )}
              {allocation.approval_type === 'automatic' && (
                <p><Clock3 size={17} /> {t('payment_release.auto_approved', { date: exactDate(allocation.approved_at) })}</p>
              )}
              {role === 'business' && isUnderReview && (
                <div className={styles.actions}>
                  <button
                    disabled={busyId === allocation.id || !isDeliverablesComplete}
                    title={!isDeliverablesComplete ? t('payment_release.cannot_approve_incomplete', 'No puedes aprobar hasta que el creador complete todos los entregables requeridos.') : ''}
                    onClick={() => run(allocation.id, () => approveCreatorAllocation(allocation.id))}
                  >
                    {t('payment_release.approve')}
                  </button>
                  <button className={styles.secondary} disabled={busyId === allocation.id} onClick={() => reportIssue(allocation)}>
                    {t('payment_release.report_issue')}
                  </button>
                </div>
              )}
              {role === 'business' && isApproved && !refundOpen && new Date() <= new Date(allocation.refund_request_deadline) && (
                <button className={styles.refund} disabled={busyId === allocation.id} onClick={() => requestRefund(allocation)}>
                  {t('payment_release.request_refund')}
                </button>
              )}
              {role === 'business' && isApproved && new Date() > new Date(allocation.refund_request_deadline) && (
                <p>{t('payment_release.refund_expired', { deadline: exactDate(allocation.refund_request_deadline) })}</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default PaidCollabPaymentStatus;
